import React, { useMemo, useCallback, useState, useEffect } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { useInBreakpoints as isBreakpoint } from 'js/design_system/hooks/useBreakpoint';

import { Paper, OpenalexWork, PaperImage } from 'js/social/shared/types';
import { Carousel } from 'js/social/shared/Carousel';

import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';

type Props = {
  work: Paper | OpenalexWork;
  canUseReader: boolean;
  showReader: () => void;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
  paperInteractions: React.ReactNode;
  onImageChange: (imageIndex: number) => void;
  variant: 'default' | 'dense';
};

const PaperImagesContainer = styled.div<{ variant?: 'default' | 'dense' }>`
  padding: var(--spacing-sm-24, 24px) var(--spacing-sm-16, 16px);

  &.variant-dense {
    border-radius: 8px;
    border: 1px solid var(--stroke-light, #dddde2);
  }
`;

const SlideContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 336px;
  gap: 16px;

  padding-bottom: 24px; // TODO: replace with slider dots
`;

const SlideCaption = styled(Body)`
  @import 'stylesheets/design_system/_util.scss';
  @include text-ellipsis(2, 24px);

  flex-shrink: 0;

  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-md-size, 16px);
`;

const SlideImageContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  min-height: 0;
  padding: 0 var(--spacing-md-48, 48px);
`;

const SlideImageImpression = styled(ImpressionWatcher)`
  display: flex;
`;

const SlideImage = styled.img`
  object-fit: contain;
  max-height: 100%;
  max-width: 100%;
  cursor: pointer;
`;

const Citation = styled(Body)`
  padding: var(--spacing-sx-16, 16px) 0;
  border-top: 1px solid var(--stroke-dark, #141413);
`;

const CarouselSlide = ({
  item,
  index,
  clickableProps,
  impressionPropsGen,
}: {
  item: PaperImage;
  index: number;
  clickableProps: object;
  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
}) => {
  return (
    <SlideContainer>
      <SlideCaption size="sm">{item.caption}</SlideCaption>
      <SlideImageContainer>
        <SlideImageImpression {...impressionPropsGen(`slide-${index}`)}>
          <SlideImage src={item.url} alt={item.caption} {...clickableProps} />
        </SlideImageImpression>
      </SlideImageContainer>
    </SlideContainer>
  );
};

const PaperImages = ({
  work,
  canUseReader,
  showReader,
  impressionPropsGen,
  paperInteractions,
  onImageChange = () => null,
  variant = 'default',
}: Props) => {
  const clickableProps = useMemo(() => {
    return canUseReader
      ? {
          onClick: showReader,
        }
      : { href: work.url, target: '_blank' };
  }, [canUseReader, showReader, work]);

  const renderItem = useCallback(
    (item: PaperImage, index: number) => (
      <CarouselSlide {...{ item, index, clickableProps, impressionPropsGen }} />
    ),
    [clickableProps, impressionPropsGen],
  );

  const [arrows, setArrows] = useState(isBreakpoint(['medium', 'large']));

  useEffect(() => {
    const handleResize = () => setArrows(isBreakpoint(['medium', 'large']));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const images = work.images || [];
  if (images.length === 0) return null;

  return (
    <PaperImagesContainer variant={variant}>
      <Carousel
        items={images}
        renderItem={renderItem}
        gap={0}
        keyExtractor={(item) => item.url}
        sliderProps={{
          infinite: false,
          lazyLoad: 'ondemand',
          arrows,
          afterChange: onImageChange,
        }}
      />
      <Citation>Source: {work.citation}</Citation>
      {paperInteractions}
    </PaperImagesContainer>
  );
};

export default PaperImages;
